<script lang="ts" setup>
import { computed } from 'vue';

import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { QuinsApiUrlFormSchema } from '@modules/quints-api/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import { useCopyToClipboard } from '@modules/core/composables/copy';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { TRANSLATION_GROUPS_OF_QUINTS_API } from '@modules/quints-api/constants';
import { QuintsApiLinkValues } from '@modules/quints-api/enums';
import { useQuintsApiTokenMutation } from '@modules/quints-api/mutations';
import { useQuintsApiUrlsListQuery } from '@modules/quints-api/queries';
import { assert, isString } from '@utils/guards';

const { data, isLoading: isLoadingApiUrls } = useQuintsApiUrlsListQuery();
const { mutate: refreshToken, isLoading: isLoadingRefreshToken } = useQuintsApiTokenMutation();
const { getTranslation } = useI18n();
const { confirmDelete } = useConfirmDialogs();
const { copyToClipboard } = useCopyToClipboard();

const formSchema = computed<FormSchema<QuinsApiUrlFormSchema>>(() => ({
  affiliate_link: {
    type: EFormInputTypes.TEXTAREA,
    label: getTranslation(TRANSLATION_GROUPS_OF_QUINTS_API, 'AFFILIATES_API_LINK').value,
    value: data.value?.affiliate_link.domain,
  },
  tracker_link: {
    type: EFormInputTypes.TEXTAREA,
    label: getTranslation(TRANSLATION_GROUPS_OF_QUINTS_API, 'TRACKERS_API_LINK').value,
    value: data.value?.tracker_link.domain,
  },
}));

async function copyLink(linkName: string): Promise<void> {
  const domain = formSchema.value[linkName as keyof QuinsApiUrlFormSchema].value;
  assert(isString(domain));
  await copyToClipboard(domain);
}

function resetApiToken(event: MouseEvent): void {
  confirmDelete({
    event,
    header: getTranslation(TRANSLATION_GROUPS_OF_QUINTS_API, 'TITLE').value,
    message: getTranslation(TRANSLATION_GROUPS_OF_QUINTS_API, 'QUESTION').value,
    acceptLabel: getTranslation(TRANSLATION_GROUPS_OF_QUINTS_API, 'ANSWER_YES').value,
    rejectLabel: getTranslation(TRANSLATION_GROUPS_OF_QUINTS_API, 'ANSWER_NO').value,
    accept: () => refreshToken(),
  });
}
</script>

<template>
  <VCard v-data-qa-auto-id="'QuintsApiPage'"
    :title="$getTranslation(TRANSLATION_GROUPS_OF_QUINTS_API, 'HEADER')"
    type="form"
  >
    <VLoader
      :loading="isLoadingApiUrls"
      min-height="400px"
    >
      <VForm
        :schema="formSchema"
        readonly
      >
        <template #secondaryBtn>
          <div class="flex flex-col gap-2 sm:flex-row">
            <VButton
              :label="$getTranslation(TRANSLATION_GROUPS_OF_QUINTS_API, 'AFFILIATES_COPY_LINK')"
              @click="copyLink(QuintsApiLinkValues.AFFILIATE)"
            />
            <VButton
              :label="$getTranslation(TRANSLATION_GROUPS_OF_QUINTS_API, 'TRACKERS_COPY_LINK')"
              @click="copyLink(QuintsApiLinkValues.TRACKERS)"
            />
            <VButton
              :disabled="isLoadingRefreshToken"
              :label="$getTranslation(TRANSLATION_GROUPS_OF_QUINTS_API, 'RESET_BUTTON')"
              @click="resetApiToken"
            />
          </div>
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
